import { useMutation } from "@tanstack/react-query";
import axiosInstance from "../config/axiosConfig";
import useConfigStore from "../store/configStore";
import type { ProjectUpdateRequest, ProjectUpdateResponse, ProjectEntity, ProjectItem } from "../types/project";
import { createQueryString } from "../utils/helper";
import { generateComplianceReportPDFDownload } from "../services/pdfService";

type getAllProjectsPayload = {
  filters: {
    page: string | number;
    limit: string | number;
    start_created_on?: string;
    end_created_on?: string;
    start_updated_on?: string;
    end_updated_on?: string;
    sortField?: string;
    sortOrder?: string;
    search?: string;
    project_client_name?: string[];
    project_location?: string[];
  };
};

interface CreateProjectPayload {
  project_name: string;
  project_client_name?: string;
  project_description?: string;
  project_location?: string;
  project_services?: string[];
  project_completion_date?: string;
  project_square_footage?: string;
  project_cost?: string;
  project_revit_id?: string;
  project_zonal_links?: string[];
  project_start_date?: string;
}

export interface GenerateProjectDescriptionPayload {
  project_name: string;
  client_name?: string;
  completion_date?: string;
  included_services?: string[];
  total_area?: string;
  budget?: string;
  location?: string;
  description?: string;
}

export interface GenerateProjectDescriptionResponse {
  status: string;
  message: string;
  data: string;
}

// Helper function to normalize project data from both response structures
const normalizeProjectData = (response: any): ProjectItem[] => {
  // Check if it's the new entities structure
  if (response.entities && Array.isArray(response.entities)) {
    return response.entities.map((entity: ProjectEntity) => ({
      key: entity.key,
      value: entity.value,
    }));
  }
  
  // Check if it's the old structure
  if (response.data && response.data.list && Array.isArray(response.data.list)) {
    return response.data.list.map((project: any) => ({
      _id: project._id,
      project_name: project.project_name,
      project_client_name: project.project_client_name || null,
      project_location: project.project_location || null,
      project_services: project.project_services || [],
      project_completion_date: project.project_completion_date || null,
      project_square_footage: project.project_square_footage || null,
      project_cost: project.project_cost || null,
      project_description: project.project_description || null,
      project_year: project.project_year || new Date().getFullYear().toString(),
      project_start_date: project.project_start_date || null,
      project_created_at: project.project_created_at,
      project_last_updated: project.project_last_updated || null,
      __v: project.__v || 0,
      project_revit_id: project.project_revit_id || null,
      revit_file_project_id: project.revit_file_project_id || null,
      revit_file__name: project.revit_file__name || null,
      project_revit_json_synced: project.project_revit_json_synced || null,
      storage_id: project.storage_id || null,
    }));
  }
  
  // Return empty array if neither structure is found
  return [];
};

export const get_all_projects = async (payload?: getAllProjectsPayload) => {
  let queryString = "";
  if (payload) {
    const { filters } = payload;
    // make query string
    queryString = createQueryString(filters);
  }
  const endpoint = useConfigStore.getState().getEndpoint('getProjects');
  const { data } = await axiosInstance.get(
    `${endpoint}?${queryString}`,
    // {
    //   withCredentials: true, // Ensure credentials are included
    // }
  );
  
  // Normalize the response data to handle both structures
  const normalizedProjects = normalizeProjectData(data);
  
  // Return a consistent structure regardless of the original response format
  return {
    projects: normalizedProjects,
    isNewFormat: !!(data.entities && Array.isArray(data.entities)),
    originalResponse: data, // Keep original response for debugging
  };
};

export const get_project = async (payload: { project_id: string }) => {
  const endpoint = useConfigStore.getState().getEndpoint('getProject');
  const { data } = await axiosInstance.get(
    `${endpoint}/${payload.project_id}`,
    // {
    //   withCredentials: true,
    // }
  );
  return data;
};

export const delete_project = async ({
  project_id,
}: {
  project_id: string | number;
}) => {
  const endpoint = useConfigStore.getState().getEndpoint('deleteProject');
  const { data } = await axiosInstance.delete(
    `${endpoint}/${project_id}`,
    // {
    //   withCredentials: true,
    // }
  );
  return data;
};

// API call for updating the project
export const updateProject = async (
  projectId: string,
  data: ProjectUpdateRequest
): Promise<ProjectUpdateResponse> => {
  const endpoint = useConfigStore.getState().getEndpoint('updateProject');
  const response = await axiosInstance.put(
    `${endpoint}/${projectId}`,
    data
  );
  return response.data;
};

// Service to generate project description
export const generateProjectDescription = async (
  data: GenerateProjectDescriptionPayload
): Promise<GenerateProjectDescriptionResponse> => {
  const endpoint = useConfigStore.getState().getEndpoint('generateDescription');
  const response = await axiosInstance.post(
    endpoint,
    data
  );
  return response.data;
};

export const useGenerateProjectDescription = () => {
  const mutation = useMutation<
    GenerateProjectDescriptionResponse,
    Error,
    GenerateProjectDescriptionPayload
  >({ mutationFn: generateProjectDescription });

  return mutation;
};

export const create_new_project = async (data: CreateProjectPayload) => {
  const endpoint = useConfigStore.getState().getEndpoint('createProject');
  const response = await axiosInstance.post(
    endpoint,
    data
  );
  return response.data;
};

// Service function to fetch all locations
export const getAllLocations = async () => {
  try {
    const endpoint = useConfigStore.getState().getEndpoint('locations');
    const response = await axiosInstance.get(endpoint);
    return response.data.data.locations;
  } catch (error) {
    console.error("Error fetching locations:", error);
    throw new Error("Error fetching locations");
  }
};

// Service function to fetch all clients
export const getAllClientNames = async () => {
  try {
    const endpoint = useConfigStore.getState().getEndpoint('clientNames');
    const response = await axiosInstance.get(endpoint);
    return response.data.data.clientNames;
  } catch (error) {
    console.error("Error fetching client names:", error);
    throw new Error("Error fetching client names");
  }
};

export const getProjectNames = async (): Promise<string[]> => {
  const endpoint = useConfigStore.getState().getEndpoint('projectNames');
  const response = await axiosInstance.get(endpoint);
  // Handle new response format with entities array - return keys (project names)
  if (response.data.entities && Array.isArray(response.data.entities)) {
    return response.data.entities
      .flatMap((entity: any) => {
        const key = entity?.key;
        return typeof key === 'string' && key.length > 0 ? [key] : [];
      });
  }
  
  // Handle old response format with projectNames array
  if (response.data.data && response.data.data.projectNames && Array.isArray(response.data.data.projectNames)) {
    return response.data.data.projectNames
      .flatMap((name: any) => typeof name === 'string' && name.length > 0 ? [name] : []);
  }
  
  // Return empty array if neither format is found
  console.warn('Unexpected response format from getProjectNames:', response.data);
  return [];

};

// Service to fetch Revit sheets for a project
export const getRevitSheets = async (project_id: string) => {
  const endpoint = useConfigStore.getState().getEndpoint('getRevitSheets');
  const response = await axiosInstance.get(
    `${endpoint}/${project_id}`
  );
  return response.data.data;
};

// Service to fetch Revit views for a project
export const getRevitViews = async (project_id: string) => {
  const endpoint = useConfigStore.getState().getEndpoint('getRevitViews');
  const response = await axiosInstance.get(
    `${endpoint}/${project_id}`
  );
  return response.data.data;
};

export const getRevitSections = async (projectId: string) => {
  const endpoint = useConfigStore.getState().getEndpoint('getRevitSections');
  const response = await axiosInstance.get(
    `${endpoint}/${projectId}`
  );
  return response.data.data.result;
};

export const downloadComplianceReportPDF = async (
  complianceMessageId: string,
  projectId: string
) => {
  // Use frontend PDF generation with download capability
  return await generateComplianceReportPDFDownload(
    complianceMessageId,
    projectId
  );
};

// Service to fetch four images by project
export const getFourImagesByProject = async (project_id: string) => {
  const endpoint = useConfigStore.getState().getEndpoint('getFourImagesByProject');
  const response = await axiosInstance.get(
    `${endpoint}/${project_id}`
  );
  return response.data;
};

export const getZolaDetails = async (projectId: string) => {
  const endpoint = useConfigStore.getState().getEndpoint('getZolaDetails');
  const response = await axiosInstance.get(
    `${endpoint}/${projectId}`
  );
  return response.data;
};
